import { useState, useEffect } from "react";
import {
    Button,
    Select,
    SelectItem,
    Input,
    DatePicker,
    DateRangePicker,
} from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { LuPlus, LuTrash2 } from "react-icons/lu";
import { useFetchData } from "@/hooks";

const FILTER_FIELDS = [
    {
        key: "date",
        label: "Tanggal",
        type: "date",
        operators: [
            { key: "sama_dengan", label: "Sama dengan", needsValue: true },
            { key: "sebelum", label: "Sebelum", needsValue: true },
            { key: "sesudah", label: "Sesudah", needsValue: true },
            { key: "antara", label: "Antara", needsValue: "range" },
            { key: "ada", label: "Ada", needsValue: false },
            { key: "kosong", label: "Kosong", needsValue: false },
        ],
    },
    {
        key: "journal_number",
        label: "Nomor Jurnal",
        type: "text",
        operators: [
            { key: "mengandung", label: "Mengandung", needsValue: true },
            {
                key: "tidak_mengandung",
                label: "Tidak mengandung",
                needsValue: true,
            },
            { key: "sama_dengan", label: "Sama dengan", needsValue: true },
            {
                key: "dimulai_dengan",
                label: "Dimulai dengan",
                needsValue: true,
            },
            {
                key: "diakhiri_dengan",
                label: "Diakhiri dengan",
                needsValue: true,
            },
            { key: "ada", label: "Ada", needsValue: false },
            { key: "kosong", label: "Kosong", needsValue: false },
        ],
    },
    {
        key: "description",
        label: "Keterangan",
        type: "text",
        operators: [
            { key: "mengandung", label: "Mengandung", needsValue: true },
            {
                key: "tidak_mengandung",
                label: "Tidak mengandung",
                needsValue: true,
            },
            { key: "sama_dengan", label: "Sama dengan", needsValue: true },
            {
                key: "dimulai_dengan",
                label: "Dimulai dengan",
                needsValue: true,
            },
            {
                key: "diakhiri_dengan",
                label: "Diakhiri dengan",
                needsValue: true,
            },
            { key: "ada", label: "Ada", needsValue: false },
            { key: "kosong", label: "Kosong", needsValue: false },
        ],
    },
    {
        key: "journal_account",
        label: "Akun Jurnal",
        type: "select",
        operators: [
            {
                key: "sama_dengan",
                label: "Sama dengan",
                needsValue: "select",
            },
            {
                key: "tidak_sama_dengan",
                label: "Tidak sama dengan",
                needsValue: "select",
            },
            { key: "mengandung", label: "Mengandung", needsValue: true },
            {
                key: "tidak_mengandung",
                label: "Tidak mengandung",
                needsValue: true,
            },
            { key: "ada", label: "Ada", needsValue: false },
            { key: "kosong", label: "Kosong", needsValue: false },
        ],
    },
];

const createEmptyRule = () => ({
    id: crypto.randomUUID(),
    field: "",
    operator: "",
    value: "",
    value2: "",
});

const RuleFilterPanel = ({ isOpen, currentRulesJson, onApply, onClear }) => {
    const [rules, setRules] = useState([]);
    const { data: journalAccounts } = useFetchData(
        "select/journal-account",
        {},
        true,
    );

    // Parse existing rules when panel opens
    useEffect(() => {
        if (isOpen) {
            if (currentRulesJson) {
                try {
                    const parsed = JSON.parse(currentRulesJson);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        setRules(
                            parsed.map((r) => ({
                                ...r,
                                id: crypto.randomUUID(),
                            })),
                        );
                        return;
                    }
                } catch {
                    // ignore
                }
            }
            setRules([]);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const addRule = () => {
        setRules((prev) => [...prev, createEmptyRule()]);
    };

    const removeRule = (id) => {
        setRules((prev) => prev.filter((r) => r.id !== id));
    };

    const updateRule = (id, key, val) => {
        setRules((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const updated = { ...r, [key]: val };
                // Reset operator & value when field changes
                if (key === "field") {
                    updated.operator = "";
                    updated.value = "";
                    updated.value2 = "";
                }
                // Reset value when operator changes
                if (key === "operator") {
                    updated.value = "";
                    updated.value2 = "";
                }
                return updated;
            }),
        );
    };

    const getFieldConfig = (fieldKey) =>
        FILTER_FIELDS.find((f) => f.key === fieldKey);

    const getOperatorConfig = (fieldKey, operatorKey) => {
        const field = getFieldConfig(fieldKey);
        return field?.operators.find((o) => o.key === operatorKey);
    };

    const handleApply = () => {
        // Filter out incomplete rules
        const validRules = rules
            .filter((r) => {
                if (!r.field || !r.operator) return false;
                const opConfig = getOperatorConfig(r.field, r.operator);
                if (!opConfig) return false;
                if (opConfig.needsValue === false) return true;
                if (opConfig.needsValue === "range") return r.value && r.value2;
                return r.value;
            })
            .map(({ id, ...rest }) => rest); // Strip id

        onApply(validRules.length > 0 ? JSON.stringify(validRules) : "");
    };

    const handleClear = () => {
        setRules([]);
        onClear();
    };

    const renderValueInput = (rule) => {
        const opConfig = getOperatorConfig(rule.field, rule.operator);
        if (!opConfig || opConfig.needsValue === false) return null;

        const fieldConfig = getFieldConfig(rule.field);

        // Date range (antara)
        if (opConfig.needsValue === "range") {
            const rangeValue =
                rule.value && rule.value2
                    ? {
                          start: parseDate(rule.value),
                          end: parseDate(rule.value2),
                      }
                    : null;

            return (
                <DateRangePicker
                    size="sm"
                    className="w-72"
                    value={rangeValue}
                    onChange={(value) => {
                        if (value) {
                            updateRule(
                                rule.id,
                                "value",
                                value.start.toString(),
                            );
                            updateRule(rule.id, "value2", value.end.toString());
                        } else {
                            updateRule(rule.id, "value", "");
                            updateRule(rule.id, "value2", "");
                        }
                    }}
                    variant="flat"
                />
            );
        }

        // Journal account select
        if (opConfig.needsValue === "select") {
            return (
                <Select
                    size="sm"
                    className="w-60"
                    placeholder="Pilih akun..."
                    selectedKeys={rule.value ? [String(rule.value)] : []}
                    onSelectionChange={(keys) => {
                        const val = Array.from(keys)[0] || "";
                        updateRule(rule.id, "value", val);
                    }}
                >
                    {(journalAccounts || []).map((acc) => (
                        <SelectItem key={String(acc.value)}>
                            {acc.title}
                        </SelectItem>
                    ))}
                </Select>
            );
        }

        // Date input
        if (fieldConfig?.type === "date") {
            return (
                <DatePicker
                    size="sm"
                    className="w-48"
                    value={rule.value ? parseDate(rule.value) : null}
                    onChange={(value) => {
                        updateRule(
                            rule.id,
                            "value",
                            value ? value.toString() : "",
                        );
                    }}
                    variant="flat"
                    showMonthAndYearPickers
                />
            );
        }

        // Text input
        return (
            <Input
                size="sm"
                className="w-60"
                placeholder="Masukkan nilai..."
                value={rule.value}
                onValueChange={(val) => updateRule(rule.id, "value", val)}
            />
        );
    };

    return (
        <div className="border-default-200 flex flex-col gap-3 rounded-lg border p-4">
            {rules.length === 0 && (
                <p className="text-default-400 text-sm">
                    Belum ada aturan filter. Klik tombol di bawah untuk
                    menambahkan.
                </p>
            )}

            {rules.map((rule) => {
                const fieldConfig = getFieldConfig(rule.field);
                const operators = fieldConfig?.operators || [];

                return (
                    <div key={rule.id} className="flex items-center gap-2">
                        {/* Field */}
                        <Select
                            size="sm"
                            className="w-44"
                            placeholder="Pilih field..."
                            selectedKeys={rule.field ? [rule.field] : []}
                            onSelectionChange={(keys) => {
                                const val = Array.from(keys)[0] || "";
                                updateRule(rule.id, "field", val);
                            }}
                        >
                            {FILTER_FIELDS.map((f) => (
                                <SelectItem key={f.key}>{f.label}</SelectItem>
                            ))}
                        </Select>

                        {/* Operator */}
                        <Select
                            size="sm"
                            className="w-48"
                            placeholder="Pilih operator..."
                            selectedKeys={rule.operator ? [rule.operator] : []}
                            isDisabled={!rule.field}
                            onSelectionChange={(keys) => {
                                const val = Array.from(keys)[0] || "";
                                updateRule(rule.id, "operator", val);
                            }}
                        >
                            {operators.map((o) => (
                                <SelectItem key={o.key}>{o.label}</SelectItem>
                            ))}
                        </Select>

                        {/* Value */}
                        {rule.operator && renderValueInput(rule)}

                        {/* Remove */}
                        <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            color="danger"
                            onPress={() => removeRule(rule.id)}
                        >
                            <LuTrash2 size={14} />
                        </Button>
                    </div>
                );
            })}

            <div className="flex items-center justify-between">
                <Button
                    size="sm"
                    variant="flat"
                    startContent={<LuPlus size={14} />}
                    onPress={addRule}
                >
                    Tambah Rule
                </Button>

                <div className="flex items-center gap-2">
                    {rules.length > 0 && (
                        <Button
                            size="sm"
                            variant="light"
                            color="danger"
                            onPress={handleClear}
                        >
                            Reset
                        </Button>
                    )}
                    <Button
                        size="sm"
                        color="primary"
                        onPress={handleApply}
                        isDisabled={rules.length === 0}
                    >
                        Terapkan
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default RuleFilterPanel;
